// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sprintf } from 'sprintf-js';

import { CurrentUser } from 'api/currentUser';
import { Notification } from 'api/userNotifications';
import { Box } from 'components/Box';
import { Notifications } from 'components/Notifications';
import {
  PageHeaderDropdownMenu,
  PageHeaderDropdownMenuItem,
  PageHeaderDropdownMenuSection,
  PageHeaderDropdownMenuSectionItemRouter,
  PageHeaderDropdownMenuSectionTitle,
} from 'components/Page/Header/PageHeaderDropdownMenu';
import {
  dismissNotifications,
  fetchNotifications,
} from 'store/main/ducks/userNotifications';
import { useMainDispatch } from 'store/main/hooks/useMainDispatch';
import { colors, transition } from 'theme';
import { trackClicked, trackEvent } from 'utils/analytics';
import { hasUsedAts } from 'utils/ats';
import {
  NOTIFICATIONS,
  dismissNotification as dismissLocalNotification,
  notificationIsDismissed,
} from 'utils/localNotifications';
import { hasResumeDb, promoteResumeDatabase } from 'utils/resumeDatabase';
import { promoteSalarySurvey } from 'utils/salarySurvey';

import { PageHeaderAccountDropdownBadge } from './PageHeaderAccountDropdownBadge';
import { PageHeaderAccountDropdownConditionalMenuItem } from './PageHeaderAccountDropdownConditionalMenuItem';
import { PageHeaderAccountDropdownProfileSection } from './PageHeaderAccountDropdownProfileSection';
import { PageHeaderAtsCtaNotification } from './PageHeaderAtsCtaNotification';

const NAV_WIDTH = {
  en: 900,
  es: 1050,
  pt: 1000,
};

const getCount = (notifications: Array<Notification>) =>
  notifications.reduce(
    (acc, n) =>
      n.type === 'NEW_APPLICATIONS_BY_LISTING' ? acc + n.data.count : acc + 1,
    0
  );

export function PageHeaderAccountDropdown({
  currentUser,
  isStaff,
}: {
  currentUser: CurrentUser;
  isStaff: boolean;
}) {
  const dispatch = useMainDispatch();
  const { userNotifications } = useSelector((state) => ({
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'userNotifications' does not exist on typ... Remove this comment to see the full error message
    userNotifications: state.userNotifications,
  }));
  const [atsNotificationDismissed, setAtsNotificationDismissed] = useState(
    notificationIsDismissed(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA)
  );
  const isAdmin = currentUser.orgs && currentUser.orgs.length > 0;
  const promoteResumeDb = promoteResumeDatabase({
    currentUser,
  });
  const showSalarySurveyLink = promoteSalarySurvey({
    currentUser,
  });
  const promoteAts = isAdmin && !atsNotificationDismissed && !hasUsedAts();
  const showBadge =
    promoteResumeDb ||
    promoteAts ||
    (userNotifications.notifications &&
      userNotifications.notifications.length > 0);

  const dismissAtsNotification = () => {
    dismissLocalNotification(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA);
    setAtsNotificationDismissed(true);
  };

  const dismissNotification = (ids) => {
    dispatch(dismissNotifications(ids));
  };

  useEffect(() => {
    if (currentUser.hasNotifications && !userNotifications.isLoaded) {
      dispatch(fetchNotifications());
    }
  }, [currentUser.hasNotifications, dispatch, userNotifications.isLoaded]);

  const openMenu = (_, isOpen) => {
    if (isOpen && showSalarySurveyLink) {
      trackEvent('Displayed: Salary Survey Ad', {
        context: 'User Menu',
      });
    }
  };

  return (
    <Box position="relative">
      <PageHeaderDropdownMenu
        aria-label={getText('Account Information')}
        onClick={openMenu}
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/no-unstable-nested-components
        title={() => (
          <Box
            display={['none', null, null, 'flex']}
            alignItems="center"
            maxWidth={[
              null,
              null,
              null,
              `calc(100vw - ${NAV_WIDTH[CURRENT_LOCALE]}px)`,
            ]}
            minWidth={[null, null, null, 32]}
          >
            <Box
              whiteSpace="noWrap"
              textOverflow="ellipsis"
              overflow="hidden"
              pl={showBadge && 'calc(1em + 10px)'}
            >
              {
                /* TRANSLATORS: user greeting at top of page */
                sprintf(getText('Hi, %(name)s!'), {
                  name: currentUser.firstName || currentUser.email,
                })
              }
            </Box>
          </Box>
        )}
        data-qa-id="nav-dropdown"
      >
        {({ closeMenu, open }) => (
          <>
            {promoteResumeDb && (
              <PageHeaderAccountDropdownProfileSection
                closeMenu={closeMenu}
                currentUser={currentUser}
              />
            )}

            {userNotifications.isLoaded && userNotifications.notifications && (
              <Notifications
                notifications={userNotifications.notifications}
                dismiss={dismissNotification}
                open={open}
              />
            )}

            {promoteAts && (
              <PageHeaderAtsCtaNotification dismiss={dismissAtsNotification} />
            )}

            {showSalarySurveyLink && (
              // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: string; to: string; isStaff: boo... Remove this comment to see the full error message
              <PageHeaderAccountDropdownConditionalMenuItem
                to="/survey"
                isStaff={isStaff}
                onClick={() => {
                  trackClicked('Clicked: Salary Survey Ad', {
                    cta_type: 'Link',
                    button_text: 'Take Our Salary Survey',
                    url: '/survey',
                    context: 'User Menu',
                  });
                }}
              >
                {getText('Take Our Salary Survey')}
              </PageHeaderAccountDropdownConditionalMenuItem>
            )}

            {hasResumeDb(currentUser) && (
              // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: string; to: string; isStaff: boo... Remove this comment to see the full error message
              <PageHeaderAccountDropdownConditionalMenuItem
                to="/dashboard/employment-profile"
                isStaff={isStaff}
                onClick={closeMenu}
              >
                {getText('Employment Profile')}
              </PageHeaderAccountDropdownConditionalMenuItem>
            )}

            {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: string; to: string; isStaff: boo... Remove this comment to see the full error message */}
            <PageHeaderAccountDropdownConditionalMenuItem
              to="/dashboard/account-settings"
              isStaff={isStaff}
              onClick={closeMenu}
            >
              {getText('My Account')}
            </PageHeaderAccountDropdownConditionalMenuItem>

            {currentUser.orgs.length > 0 && (
              <PageHeaderDropdownMenuSection>
                <PageHeaderDropdownMenuSectionTitle>
                  {getText('My Organizations')}
                </PageHeaderDropdownMenuSectionTitle>
                {currentUser.orgs.map((org) =>
                  isStaff ? (
                    <PageHeaderDropdownMenuItem
                      key={org.id}
                      href={
                        org.hasGradProgram && !org.overrideToMainDashboard
                          ? `/admin/${org.id}/grad-program`
                          : `/admin/${org.id}`
                      }
                      onClick={closeMenu}
                    >
                      {org.name}
                    </PageHeaderDropdownMenuItem>
                  ) : (
                    <PageHeaderDropdownMenuSectionItemRouter
                      key={org.id}
                      to={
                        org.hasGradProgram && !org.overrideToMainDashboard
                          ? `/admin/${org.id}/grad-program`
                          : `/admin/${org.id}`
                      }
                      onClick={closeMenu}
                    >
                      {org.name}
                    </PageHeaderDropdownMenuSectionItemRouter>
                  )
                )}
              </PageHeaderDropdownMenuSection>
            )}

            <PageHeaderDropdownMenuItem href="/logout" data-qa-id="logout-link">
              {getText('Log Out')}
            </PageHeaderDropdownMenuItem>
          </>
        )}
      </PageHeaderDropdownMenu>

      {showBadge && (
        <Box
          position="absolute"
          zIndex={2000}
          left={12}
          top={11}
          pointerEvents="none"
        >
          <PageHeaderAccountDropdownBadge
            data-qa-id={
              userNotifications.notifications &&
              userNotifications.notifications.length
                ? 'notification-count'
                : 'notification-icon'
            }
            bg={
              userNotifications.notifications &&
              userNotifications.notifications.length
                ? colors.poppy
                : colors.waterBlue
            }
            transition={`all ${transition}`}
            count={
              userNotifications.notifications &&
              userNotifications.notifications.length
                ? getCount(userNotifications.notifications)
                : null
            }
          />
        </Box>
      )}
    </Box>
  );
}
